import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import i18n from 'sharedKernel/i18n/translate/config'
import { trans } from 'sharedKernel/i18n'
import { IPublicMeetingRoomGalleryImage } from '../types'
import {
  CloseButton,
  Description,
  EquipmentText,
  GalleryContent,
  GalleryHeader,
  GalleryImage,
  GalleryMain,
  GalleryTitle,
  ImageFrame,
  InfoSection,
  NavButton,
  NavIcon,
  Overlay,
  SliderSection,
} from './style'

interface SpaceImageGalleryModalProps {
  isOpen: boolean
  roomId: string
  roomName: string
  description: string
  equipment: string[]
  images: IPublicMeetingRoomGalleryImage[]
  initialSlideIndex?: number
  onClose: () => void
}

export const SpaceImageGalleryModal = ({
  isOpen,
  roomId,
  roomName,
  description,
  equipment,
  images,
  initialSlideIndex = 0,
  onClose,
}: SpaceImageGalleryModalProps): JSX.Element | null => {
  const safeInitialSlide = Math.min(
    Math.max(initialSlideIndex, 0),
    Math.max(images.length - 1, 0)
  )
  const [currentIndex, setCurrentIndex] = useState(safeInitialSlide)

  const labels = {
    close: trans('meetingRoom.public.gallery.close'),
    equipment: trans('meetingRoom.public.gallery.equipment'),
    prevImage: trans('meetingRoom.public.gallery.prevImage'),
    nextImage: trans('meetingRoom.public.gallery.nextImage'),
    ariaLabel: i18n.t('meetingRoom.public.gallery.ariaLabel', { name: roomName }),
  }

  useEffect(() => {
    setCurrentIndex(safeInitialSlide)
  }, [roomId, safeInitialSlide])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }

      if (images.length <= 1) {
        return
      }

      if (event.key === 'ArrowLeft') {
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : images.length - 1))
      }

      if (event.key === 'ArrowRight') {
        setCurrentIndex(prev => (prev < images.length - 1 ? prev + 1 : 0))
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [images.length, isOpen, onClose])

  const handlePrev = useCallback((): void => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : images.length - 1))
  }, [images.length])

  const handleNext = useCallback((): void => {
    setCurrentIndex(prev => (prev < images.length - 1 ? prev + 1 : 0))
  }, [images.length])

  if (!isOpen || images.length === 0) {
    return null
  }

  const currentImage = images[currentIndex]
  const hasMultipleImages = images.length > 1
  const equipmentLine = equipment.join(' • ')
  const hasInfo = description !== '' || equipmentLine !== ''

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label={labels.ariaLabel}
      onClick={onClose}
    >
      <GalleryHeader onClick={event => event.stopPropagation()}>
        <CloseButton type="button" onClick={onClose} aria-label={labels.close}>
          ×
        </CloseButton>
        <GalleryTitle>{roomName}</GalleryTitle>
      </GalleryHeader>

      <GalleryContent onClick={event => event.stopPropagation()}>
        <GalleryMain>
          <SliderSection>
            <NavButton
              type="button"
              data-nav="prev"
              onClick={handlePrev}
              disabled={!hasMultipleImages}
              aria-label={labels.prevImage}
            >
              <NavIcon icon={['fas', 'chevron-left']} />
            </NavButton>

            <ImageFrame>
              <GalleryImage
                src={currentImage.url}
                alt={currentImage.alt || roomName}
                loading="lazy"
              />
            </ImageFrame>

            <NavButton
              type="button"
              data-nav="next"
              onClick={handleNext}
              disabled={!hasMultipleImages}
              aria-label={labels.nextImage}
            >
              <NavIcon icon={['fas', 'chevron-right']} />
            </NavButton>
          </SliderSection>
        </GalleryMain>

        {hasInfo && (
          <InfoSection>
            {description !== '' && <Description>{description}</Description>}
            {equipmentLine !== '' && (
              <EquipmentText>
                <strong>{labels.equipment}</strong> {equipmentLine}
              </EquipmentText>
            )}
          </InfoSection>
        )}
      </GalleryContent>
    </Overlay>,
    document.body
  )
}
